import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import SettingsIcon from '../../assets/icons/settings-icon.svg';
import SettingsService from './Settings.service';
import './Settings.css';

const EQUIPMENT_CATEGORY_MACHINE = 10001;
const EQUIPMENT_CATEGORY_TOOLS_TACKLES = 10002;

const DATA_TYPE_ID = { INTEGER: 1, TEXT: 2, LIST: 3 };

function mapApiItemToPoint(item) {
  return {
    id: item.identificationDescriptionId,
    name: item.identificationDescriptionName,
    type: item.dataTypeValue || 'TEXT',
    dataTypeId: item.dataTypeId,
    listValues: Array.isArray(item.dropdownValues) ? [...item.dropdownValues] : [],
    value: Array.isArray(item.dropdownValues) && item.dropdownValues.length ? item.dropdownValues.join(', ') : '',
    machineId: item.machineId,
    toolsTacklesId: item.toolsTacklesId,
  };
}

function mapCheckpointApiItemToPoint(item) {
  return {
    id: item.inspectionCheckpointId,
    name: item.inspectionCheckpointName,
    type: item.dataTypeValue || 'TEXT',
    dataTypeId: item.dataTypeId,
    listValues: Array.isArray(item.dropdownValues) ? [...item.dropdownValues] : [],
    value: Array.isArray(item.dropdownValues) && item.dropdownValues.length ? item.dropdownValues.join(', ') : '',
    machineId: item.machineId,
    toolsTacklesId: item.toolsTacklesId,
    mainPointId: item.mainPointId ?? null,
    mainPointName: item.mainPointName ?? '',
  };
}

/** Server orders by displayOrder ASC then mainPointId ASC; keep the same order client-side. */
function sortMainPoints(list) {
  return [...list].sort(
    (a, b) =>
      (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
      (a.mainPointId ?? 0) - (b.mainPointId ?? 0)
  );
}

function ActionSnackbar({ toast, onClose }) {
  return (
    <Snackbar
      open={!!toast?.open}
      autoHideDuration={4500}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={onClose}
        severity={toast?.severity || 'info'}
        variant="filled"
        sx={{ width: '100%', fontWeight: 600 }}
      >
        {toast?.message || ''}
      </Alert>
    </Snackbar>
  );
}

function Settings() {
  const { userData, onLogout } = useOutletContext();
  const [lastLoginTime, setLastLoginTime] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [machinesList, setMachinesList] = useState([]);
  const [toolsTacklesList, setToolsTacklesList] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [loadingToolsTackles, setLoadingToolsTackles] = useState(false);
  const [machinesError, setMachinesError] = useState(null);
  const [toolsTacklesError, setToolsTacklesError] = useState(null);
  const [points, setPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [pointsError, setPointsError] = useState(null);
  const [savingPoint, setSavingPoint] = useState(false);
  const [deletingPointId, setDeletingPointId] = useState(null);
  const POINT_TYPES = ['INTEGER', 'TEXT', 'LIST'];
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [newPointName, setNewPointName] = useState('');
  const [newPointType, setNewPointType] = useState('TEXT');
  const [newPointValue, setNewPointValue] = useState('');
  const [newPointListValues, setNewPointListValues] = useState(['']);
  const [newPointMainPointId, setNewPointMainPointId] = useState(null);
  // Main points (machine inspection checkpoints grouping layer)
  const [mainPoints, setMainPoints] = useState([]);
  const [loadingMainPoints, setLoadingMainPoints] = useState(false);
  const [mainPointsError, setMainPointsError] = useState(null);
  const [showMainPointForm, setShowMainPointForm] = useState(false);
  const [editingMainPoint, setEditingMainPoint] = useState(null);
  const [newMainPointName, setNewMainPointName] = useState('');
  const [newMainPointOrder, setNewMainPointOrder] = useState('');
  const [savingMainPoint, setSavingMainPoint] = useState(false);
  const [deletingMainPointId, setDeletingMainPointId] = useState(null);
  const [collapsedMainPoints, setCollapsedMainPoints] = useState({});
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const { userDetails, dashboardList } = userData || {};

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  useEffect(() => {
    // Get last login time from localStorage
    const stored = localStorage.getItem('lastLoginTime');
    if (stored) {
      setLastLoginTime(new Date(stored).toLocaleString());
    } else {
      const now = new Date();
      localStorage.setItem('lastLoginTime', now.toISOString());
      setLastLoginTime(now.toLocaleString());
    }
  }, []);

  // Fetch machines when Manage (Machine) panel is open
  useEffect(() => {
    if (!showManageModal || selectedSection !== 'Machine') return;
    setLoadingMachines(true);
    setMachinesError(null);
    SettingsService.getMachines(EQUIPMENT_CATEGORY_MACHINE)
      .then((data) => {
        const list = data?.machines || [];
        setMachinesList(list);
        if (list.length > 0 && selectedMachine == null) {
          setSelectedMachine(list[0].machineId);
        }
      })
      .catch((err) => setMachinesError(err.message || 'Failed to load machines'))
      .finally(() => setLoadingMachines(false));
  }, [showManageModal, selectedSection]);

  // Fetch tools & tackles when Manage (Tools And Tackles) panel is open
  useEffect(() => {
    if (!showManageModal || selectedSection !== 'Tools And Tackles') return;
    setLoadingToolsTackles(true);
    setToolsTacklesError(null);
    SettingsService.getToolsTackles(EQUIPMENT_CATEGORY_TOOLS_TACKLES)
      .then((data) => {
        const list = data?.toolsTackles || [];
        setToolsTacklesList(list);
        if (list.length > 0 && selectedMachine == null) {
          setSelectedMachine(list[0].toolsTacklesId);
        }
      })
      .catch((err) => setToolsTacklesError(err.message || 'Failed to load tools and tackles'))
      .finally(() => setLoadingToolsTackles(false));
  }, [showManageModal, selectedSection]);

  const isMachineIdentification = selectedSection === 'Machine' && selectedType === 'Identification';
  const isToolsTacklesIdentification = selectedSection === 'Tools And Tackles' && selectedType === 'Identification';
  const isMachineCheckpoints = selectedSection === 'Machine' && selectedType === 'Checkpoints';
  const isToolsTacklesCheckpoints = selectedSection === 'Tools And Tackles' && selectedType === 'Checkpoints';
  // Both checkpoint modes share the main-point grouping layer
  const isCheckpointsMode = isMachineCheckpoints || isToolsTacklesCheckpoints;

  // Fetch machine identification descriptions when Machine Identification panel is open and a machine is selected
  useEffect(() => {
    if (!showManageModal || !isMachineIdentification || selectedMachine == null) return;
    setLoadingPoints(true);
    setPointsError(null);
    SettingsService.getAllMachineIdentificationDescriptions(selectedMachine)
      .then((data) => {
        const list = data?.identificationDescriptions || [];
        setPoints(list.map(mapApiItemToPoint));
      })
      .catch((err) => {
        setPointsError(err.message || 'Failed to load identification points');
        setPoints([]);
      })
      .finally(() => setLoadingPoints(false));
  }, [showManageModal, isMachineIdentification, selectedMachine]);

  // Fetch tools & tackles identification descriptions when Tools And Tackles Identification panel is open and one is selected
  useEffect(() => {
    if (!showManageModal || !isToolsTacklesIdentification || selectedMachine == null) return;
    setLoadingPoints(true);
    setPointsError(null);
    SettingsService.getAllToolsTacklesIdentificationDescriptions(selectedMachine)
      .then((data) => {
        const list = data?.identificationDescriptions || [];
        setPoints(list.map(mapApiItemToPoint));
      })
      .catch((err) => {
        setPointsError(err.message || 'Failed to load identification points');
        setPoints([]);
      })
      .finally(() => setLoadingPoints(false));
  }, [showManageModal, isToolsTacklesIdentification, selectedMachine]);

  // Fetch machine inspection checkpoints + main points when Machine Checkpoints panel is open and a machine is selected
  useEffect(() => {
    if (!showManageModal || !isMachineCheckpoints || selectedMachine == null) return;
    setLoadingPoints(true);
    setLoadingMainPoints(true);
    setPointsError(null);
    setMainPointsError(null);
    setCollapsedMainPoints({});
    Promise.all([
      SettingsService.getAllMachineInspectionCheckpoints(selectedMachine),
      SettingsService.getAllMachineMainPoints(selectedMachine),
    ])
      .then(([checkpointsData, mainPointsData]) => {
        // Use nested checkpoints from first API
        const flattenedCheckpoints = SettingsService.flattenCheckpointsFromNestedResponse(checkpointsData);
        setPoints(flattenedCheckpoints.map(mapCheckpointApiItemToPoint));
        // Use mainPoints from second API for displayOrder and other metadata
        setMainPoints(sortMainPoints(mainPointsData?.mainPoints || []));
      })
      .catch((err) => {
        setPointsError(err.message || 'Failed to load inspection checkpoints');
        setPoints([]);
        setMainPoints([]);
      })
      .finally(() => {
        setLoadingPoints(false);
        setLoadingMainPoints(false);
      });
  }, [showManageModal, isMachineCheckpoints, selectedMachine]);

  /** Refetch machine checkpoints and main points together (after any mutation). */
  const reloadMachineCheckpointData = (machineId) =>
    Promise.all([
      SettingsService.getAllMachineInspectionCheckpoints(machineId),
      SettingsService.getAllMachineMainPoints(machineId),
    ]).then(([checkpointsData, mainPointsData]) => {
      const flattenedCheckpoints = SettingsService.flattenCheckpointsFromNestedResponse(checkpointsData);
      setPoints(flattenedCheckpoints.map(mapCheckpointApiItemToPoint));
      setMainPoints(sortMainPoints(mainPointsData?.mainPoints || []));
    });

  /** Refetch tools & tackles checkpoints and main points together (after any mutation). */
  const reloadToolsTacklesCheckpointData = (toolsTacklesId) =>
    Promise.all([
      SettingsService.getAllToolsTacklesInspectionCheckpoints(toolsTacklesId),
      SettingsService.getAllToolsTacklesMainPoints(toolsTacklesId),
    ]).then(([checkpointsData, mainPointsData]) => {
      const flattenedCheckpoints = SettingsService.flattenCheckpointsFromNestedResponse(checkpointsData);
      setPoints(flattenedCheckpoints.map(mapCheckpointApiItemToPoint));
      setMainPoints(sortMainPoints(mainPointsData?.mainPoints || []));
    });

  // Fetch tools & tackles inspection checkpoints + main points when Tools And Tackles Checkpoints panel is open and one is selected
  useEffect(() => {
    if (!showManageModal || !isToolsTacklesCheckpoints || selectedMachine == null) return;
    setLoadingPoints(true);
    setLoadingMainPoints(true);
    setPointsError(null);
    setMainPointsError(null);
    setCollapsedMainPoints({});
    Promise.all([
      SettingsService.getAllToolsTacklesInspectionCheckpoints(selectedMachine),
      SettingsService.getAllToolsTacklesMainPoints(selectedMachine),
    ])
      .then(([checkpointsData, mainPointsData]) => {
        // Use nested checkpoints from first API
        const flattenedCheckpoints = SettingsService.flattenCheckpointsFromNestedResponse(checkpointsData);
        setPoints(flattenedCheckpoints.map(mapCheckpointApiItemToPoint));
        // Use mainPoints from second API for displayOrder and other metadata
        setMainPoints(sortMainPoints(mainPointsData?.mainPoints || []));
      })
      .catch((err) => {
        setPointsError(err.message || 'Failed to load inspection checkpoints');
        setPoints([]);
        setMainPoints([]);
      })
      .finally(() => {
        setLoadingPoints(false);
        setLoadingMainPoints(false);
      });
  }, [showManageModal, isToolsTacklesCheckpoints, selectedMachine]);

  const handleManageClick = (section, type) => {
    setSelectedSection(section);
    setSelectedType(type);
    setSelectedMachine(null);
    setMachinesError(null);
    setToolsTacklesError(null);
    setShowManageModal(true);
  };

  const resetPointForm = () => {
    setNewPointName('');
    setNewPointType('TEXT');
    setNewPointValue('');
    setNewPointListValues(['']);
    setNewPointMainPointId(null);
  };

  const resetMainPointForm = () => {
    setNewMainPointName('');
    setNewMainPointOrder('');
  };

  const handleClosePanel = () => {
    setShowManageModal(false);
    setSelectedSection(null);
    setSelectedType(null);
    setShowAddForm(false);
    setEditingPoint(null);
    resetPointForm();
    setMainPoints([]);
    setMainPointsError(null);
    setShowMainPointForm(false);
    setEditingMainPoint(null);
    resetMainPointForm();
    setCollapsedMainPoints({});
  };

  // ---------- Main point handlers (machine checkpoints) ----------
  const handleAddMainPointClick = () => {
    setEditingMainPoint(null);
    setNewMainPointName('');
    // Suggest the next display order
    const maxOrder = mainPoints.reduce((max, mp) => Math.max(max, mp.displayOrder ?? 0), 0);
    setNewMainPointOrder(String(maxOrder + 1));
    setMainPointsError(null);
    setShowMainPointForm(true);
    setShowAddForm(false);
    setEditingPoint(null);
    resetPointForm();
  };

  const handleEditMainPointClick = (mainPoint) => {
    setEditingMainPoint(mainPoint);
    setNewMainPointName(mainPoint.mainPointName || '');
    setNewMainPointOrder(String(mainPoint.displayOrder ?? 1));
    setMainPointsError(null);
    setShowMainPointForm(true);
    setShowAddForm(false);
    setEditingPoint(null);
    resetPointForm();
  };

  const handleCancelMainPointForm = () => {
    setShowMainPointForm(false);
    setEditingMainPoint(null);
    resetMainPointForm();
  };

  const handleSaveMainPoint = () => {
    const name = newMainPointName.trim();
    if (!name || selectedMachine == null) return;
    const order = Math.max(1, parseInt(newMainPointOrder, 10) || 1);

    setSavingMainPoint(true);
    setMainPointsError(null);

    let promise;
    if (isToolsTacklesCheckpoints) {
      promise = editingMainPoint
        ? SettingsService.editToolsTacklesMainPoint({
            mainPointId: editingMainPoint.mainPointId,
            mainPointName: name,
            displayOrder: order,
          })
        : SettingsService.addToolsTacklesMainPoint({
            toolsTacklesId: selectedMachine,
            mainPointName: name,
            displayOrder: order,
          });
    } else {
      promise = editingMainPoint
        ? SettingsService.editMachineMainPoint({
            mainPointId: editingMainPoint.mainPointId,
            mainPointName: name,
            displayOrder: order,
          })
        : SettingsService.addMachineMainPoint({
            machineId: selectedMachine,
            mainPointName: name,
            displayOrder: order,
          });
    }

    promise
      .then(() =>
        isToolsTacklesCheckpoints
          ? reloadToolsTacklesCheckpointData(selectedMachine)
          : reloadMachineCheckpointData(selectedMachine)
      )
      .then(() => {
        setShowMainPointForm(false);
        setEditingMainPoint(null);
        resetMainPointForm();
        showToast(`Main point ${editingMainPoint ? 'updated' : 'added'} successfully!`, 'success');
      })
      .catch((err) => showToast(err.message || 'Failed to save main point', 'error'))
      .finally(() => setSavingMainPoint(false));
  };

  const handleDeleteMainPoint = (mainPointId) => {
    if (selectedMachine == null) return;
    setDeletingMainPointId(mainPointId);
    const promise = isToolsTacklesCheckpoints
      ? SettingsService.deleteToolsTacklesMainPoint(mainPointId)
      : SettingsService.deleteMachineMainPoint(mainPointId);
    promise
      .then(() =>
        isToolsTacklesCheckpoints
          ? reloadToolsTacklesCheckpointData(selectedMachine)
          : reloadMachineCheckpointData(selectedMachine)
      )
      .then(() => showToast('Main point deleted successfully!', 'success'))
      .catch((err) => showToast(err.message || 'Failed to delete main point', 'error'))
      .finally(() => setDeletingMainPointId(null));
  };

  const toggleMainPointCollapse = (mainPointId) => {
    setCollapsedMainPoints((prev) => ({ ...prev, [mainPointId]: !prev[mainPointId] }));
  };

  const handleAddCheckpointUnderMainPoint = (mainPointId) => {
    setEditingPoint(null);
    resetPointForm();
    setNewPointMainPointId(mainPointId);
    setShowMainPointForm(false);
    setEditingMainPoint(null);
    setShowAddForm(true);
  };

  const handleEditPoint = (point) => {
    if (isMachineIdentification && point.id) {
      setPointsError(null);
      SettingsService.getMachineIdentificationDescription(point.id)
        .then((item) => {
          const p = mapApiItemToPoint(item);
          setEditingPoint(p);
          setNewPointName(p.name);
          setNewPointType(p.type || 'TEXT');
          setNewPointValue(p.type === 'LIST' ? '' : (p.value || ''));
          if (p.type === 'LIST') {
            setNewPointListValues(
              Array.isArray(p.listValues) && p.listValues.length ? [...p.listValues] : ['']
            );
          } else {
            setNewPointListValues(['']);
          }
          setShowAddForm(true);
        })
        .catch((err) => setPointsError(err.message || 'Failed to load point for editing'));
      return;
    }
    if (isToolsTacklesIdentification && point.id) {
      setPointsError(null);
      SettingsService.getToolsTacklesIdentificationDescription(point.id)
        .then((item) => {
          const p = mapApiItemToPoint(item);
          setEditingPoint(p);
          setNewPointName(p.name);
          setNewPointType(p.type || 'TEXT');
          setNewPointValue(p.type === 'LIST' ? '' : (p.value || ''));
          if (p.type === 'LIST') {
            setNewPointListValues(
              Array.isArray(p.listValues) && p.listValues.length ? [...p.listValues] : ['']
            );
          } else {
            setNewPointListValues(['']);
          }
          setShowAddForm(true);
        })
        .catch((err) => setPointsError(err.message || 'Failed to load point for editing'));
      return;
    }
    if (isMachineCheckpoints && point.id) {
      setPointsError(null);
      SettingsService.getMachineInspectionCheckpoint(point.id)
        .then((item) => {
          const p = mapCheckpointApiItemToPoint(item);
          setEditingPoint(p);
          setNewPointName(p.name);
          setNewPointType(p.type || 'TEXT');
          setNewPointValue(p.type === 'LIST' ? '' : (p.value || ''));
          setNewPointMainPointId(p.mainPointId ?? null);
          if (p.type === 'LIST') {
            setNewPointListValues(
              Array.isArray(p.listValues) && p.listValues.length ? [...p.listValues] : ['']
            );
          } else {
            setNewPointListValues(['']);
          }
          setShowMainPointForm(false);
          setEditingMainPoint(null);
          setShowAddForm(true);
        })
        .catch((err) => setPointsError(err.message || 'Failed to load checkpoint for editing'));
      return;
    }
    if (isToolsTacklesCheckpoints && point.id) {
      setPointsError(null);
      SettingsService.getToolsTacklesInspectionCheckpoint(point.id)
        .then((item) => {
          const p = mapCheckpointApiItemToPoint(item);
          setEditingPoint(p);
          setNewPointName(p.name);
          setNewPointType(p.type || 'TEXT');
          setNewPointValue(p.type === 'LIST' ? '' : (p.value || ''));
          setNewPointMainPointId(p.mainPointId ?? null);
          if (p.type === 'LIST') {
            setNewPointListValues(
              Array.isArray(p.listValues) && p.listValues.length ? [...p.listValues] : ['']
            );
          } else {
            setNewPointListValues(['']);
          }
          setShowMainPointForm(false);
          setEditingMainPoint(null);
          setShowAddForm(true);
        })
        .catch((err) => setPointsError(err.message || 'Failed to load checkpoint for editing'));
      return;
    }
    setEditingPoint(point);
    setNewPointName(point.name);
    setNewPointType(point.type || 'TEXT');
    setNewPointValue(point.type === 'LIST' ? '' : (point.value || ''));
    if (point.type === 'LIST') {
      if (Array.isArray(point.listValues) && point.listValues.length) {
        setNewPointListValues([...point.listValues]);
      } else if (point.value && typeof point.value === 'string') {
        setNewPointListValues(point.value.split(',').map(s => s.trim()).filter(Boolean).length ? point.value.split(',').map(s => s.trim()) : ['']);
      } else {
        setNewPointListValues(['']);
      }
    } else {
      setNewPointListValues(['']);
    }
    setShowAddForm(true);
  };

  const handleDeletePoint = (pointId) => {
    if (isMachineIdentification && selectedMachine != null) {
      setDeletingPointId(pointId);
      SettingsService.deleteMachineIdentificationDescription(pointId)
        .then(() => SettingsService.getAllMachineIdentificationDescriptions(selectedMachine))
        .then((data) => {
          const list = data?.identificationDescriptions || [];
          setPoints(list.map(mapApiItemToPoint));
          showToast('Identification description deleted successfully!', 'success');
        })
        .catch((err) => showToast(err.message || 'Failed to delete', 'error'))
        .finally(() => setDeletingPointId(null));
      return;
    }
    if (isToolsTacklesIdentification && selectedMachine != null) {
      setDeletingPointId(pointId);
      SettingsService.deleteToolsTacklesIdentificationDescription(pointId)
        .then(() => SettingsService.getAllToolsTacklesIdentificationDescriptions(selectedMachine))
        .then((data) => {
          const list = data?.identificationDescriptions || [];
          setPoints(list.map(mapApiItemToPoint));
          showToast('Identification description deleted successfully!', 'success');
        })
        .catch((err) => showToast(err.message || 'Failed to delete', 'error'))
        .finally(() => setDeletingPointId(null));
      return;
    }
    if (isMachineCheckpoints && selectedMachine != null) {
      setDeletingPointId(pointId);
      SettingsService.deleteMachineInspectionCheckpoint(pointId)
        .then(() => reloadMachineCheckpointData(selectedMachine))
        .then(() => showToast('Inspection checkpoint deleted successfully!', 'success'))
        .catch((err) => showToast(err.message || 'Failed to delete', 'error'))
        .finally(() => setDeletingPointId(null));
      return;
    }
    if (isToolsTacklesCheckpoints && selectedMachine != null) {
      setDeletingPointId(pointId);
      SettingsService.deleteToolsTacklesInspectionCheckpoint(pointId)
        .then(() => reloadToolsTacklesCheckpointData(selectedMachine))
        .then(() => showToast('Inspection checkpoint deleted successfully!', 'success'))
        .catch((err) => showToast(err.message || 'Failed to delete', 'error'))
        .finally(() => setDeletingPointId(null));
      return;
    }
    setPoints(points.filter((p) => p.id !== pointId));
  };

  const isToolsAndTacklesSection = selectedSection === 'Tools And Tackles';
  const machineSelectOptions = isToolsAndTacklesSection
    ? (toolsTacklesList || []).map((t) => ({ id: t.toolsTacklesId, name: t.toolsTacklesName }))
    : (machinesList || []).map((m) => ({ id: m.machineId, name: m.machineName }));

  const sectionValueDisplay = isToolsAndTacklesSection
    ? (toolsTacklesList.find((t) => t.toolsTacklesId === selectedMachine)?.toolsTacklesName ?? 'Tools & Tackles')
    : (machinesList.find((m) => m.machineId === selectedMachine)?.machineName ?? '');

  const pointsForCurrentMachine =
    isMachineIdentification || isToolsTacklesIdentification || isMachineCheckpoints || isToolsTacklesCheckpoints
      ? points
      : points.filter((p) => (p.machine || '') === sectionValueDisplay);

  // Group checkpoints under their main points (server sorts main points; keep that order)
  const groupedMainPoints = isCheckpointsMode
    ? mainPoints.map((mp) => ({
        ...mp,
        checkpoints: points.filter((p) => p.mainPointId === mp.mainPointId),
      }))
    : [];
  // Legacy checkpoints created before main points existed (no mainPointId)
  const ungroupedCheckpoints = isCheckpointsMode
    ? points.filter(
        (p) => p.mainPointId == null || !mainPoints.some((mp) => mp.mainPointId === p.mainPointId)
      )
    : [];

  const handlePointTypeChange = (e) => {
    const t = e.target.value;
    setNewPointType(t);
    if (t === 'LIST') {
      if (newPointValue.trim()) setNewPointListValues([newPointValue.trim()]);
      else setNewPointListValues(['']);
      setNewPointValue('');
    } else {
      setNewPointListValues(['']);
      if (newPointListValues.length === 1 && newPointListValues[0].trim()) setNewPointValue(newPointListValues[0].trim());
    }
  };

  const handleListValueChange = (index, val) => {
    const next = [...newPointListValues];
    next[index] = val;
    setNewPointListValues(next);
  };

  const addListRow = () => {
    setNewPointListValues([...newPointListValues, '']);
  };

  const removeListRow = (index) => {
    if (newPointListValues.length <= 1) return;
    setNewPointListValues(newPointListValues.filter((_, i) => i !== index));
  };

  const handleSavePoint = () => {
    const name = newPointName.trim();
    if (!name) return;

    if (isMachineIdentification && selectedMachine != null) {
      const dataTypeId = DATA_TYPE_ID[newPointType] ?? DATA_TYPE_ID.TEXT;
      const dataTypeValue = newPointType;
      const dropdownValues = newPointType === 'LIST'
        ? newPointListValues.map((s) => s.trim()).filter(Boolean)
        : [];

      setSavingPoint(true);
      setPointsError(null);

      const promise = editingPoint
        ? SettingsService.editMachineIdentificationDescription({
            identificationDescriptionId: editingPoint.id,
            identificationDescriptionName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          })
        : SettingsService.addMachineIdentificationDescription({
            machineId: selectedMachine,
            identificationDescriptionName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          });

      promise
        .then(() => SettingsService.getAllMachineIdentificationDescriptions(selectedMachine))
        .then((data) => {
          const list = data?.identificationDescriptions || [];
          setPoints(list.map(mapApiItemToPoint));
          setShowAddForm(false);
          setEditingPoint(null);
          resetPointForm();
          showToast(`Identification description ${editingPoint ? 'updated' : 'added'} successfully!`, 'success');
        })
        .catch((err) => showToast(err.message || 'Failed to save', 'error'))
        .finally(() => setSavingPoint(false));
      return;
    }

    if (isToolsTacklesIdentification && selectedMachine != null) {
      const dataTypeId = DATA_TYPE_ID[newPointType] ?? DATA_TYPE_ID.TEXT;
      const dataTypeValue = newPointType;
      const dropdownValues = newPointType === 'LIST'
        ? newPointListValues.map((s) => s.trim()).filter(Boolean)
        : [];

      setSavingPoint(true);
      setPointsError(null);

      const promise = editingPoint
        ? SettingsService.editToolsTacklesIdentificationDescription({
            identificationDescriptionId: editingPoint.id,
            identificationDescriptionName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          })
        : SettingsService.addToolsTacklesIdentificationDescription({
            toolsTacklesId: selectedMachine,
            identificationDescriptionName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          });

      promise
        .then(() => SettingsService.getAllToolsTacklesIdentificationDescriptions(selectedMachine))
        .then((data) => {
          const list = data?.identificationDescriptions || [];
          setPoints(list.map(mapApiItemToPoint));
          setShowAddForm(false);
          setEditingPoint(null);
          resetPointForm();
          showToast(`Identification description ${editingPoint ? 'updated' : 'added'} successfully!`, 'success');
        })
        .catch((err) => showToast(err.message || 'Failed to save', 'error'))
        .finally(() => setSavingPoint(false));
      return;
    }

    if (isMachineCheckpoints && selectedMachine != null) {
      // A checkpoint must live under a main point
      if (newPointMainPointId == null) {
        setPointsError('Please select a main point for this checkpoint.');
        return;
      }
      const dataTypeId = DATA_TYPE_ID[newPointType] ?? DATA_TYPE_ID.TEXT;
      const dataTypeValue = newPointType;
      const dropdownValues = newPointType === 'LIST'
        ? newPointListValues.map((s) => s.trim()).filter(Boolean)
        : [];

      setSavingPoint(true);
      setPointsError(null);

      const promise = editingPoint
        ? SettingsService.editMachineInspectionCheckpoint({
            inspectionCheckpointId: editingPoint.id,
            mainPointId: newPointMainPointId,
            inspectionCheckpointName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          })
        : SettingsService.addMachineInspectionCheckpoint({
            machineId: selectedMachine,
            mainPointId: newPointMainPointId,
            inspectionCheckpointName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          });

      promise
        .then(() => reloadMachineCheckpointData(selectedMachine))
        .then(() => {
          setShowAddForm(false);
          setEditingPoint(null);
          resetPointForm();
          showToast(`Inspection checkpoint ${editingPoint ? 'updated' : 'added'} successfully!`, 'success');
        })
        .catch((err) => showToast(err.message || 'Failed to save', 'error'))
        .finally(() => setSavingPoint(false));
      return;
    }

    if (isToolsTacklesCheckpoints && selectedMachine != null) {
      // A checkpoint must live under a main point
      if (newPointMainPointId == null) {
        setPointsError('Please select a main point for this checkpoint.');
        return;
      }
      const dataTypeId = DATA_TYPE_ID[newPointType] ?? DATA_TYPE_ID.TEXT;
      const dataTypeValue = newPointType;
      const dropdownValues = newPointType === 'LIST'
        ? newPointListValues.map((s) => s.trim()).filter(Boolean)
        : [];

      setSavingPoint(true);
      setPointsError(null);

      const promise = editingPoint
        ? SettingsService.editToolsTacklesInspectionCheckpoint({
            inspectionCheckpointId: editingPoint.id,
            mainPointId: newPointMainPointId,
            inspectionCheckpointName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          })
        : SettingsService.addToolsTacklesInspectionCheckpoint({
            toolsTacklesId: selectedMachine,
            mainPointId: newPointMainPointId,
            inspectionCheckpointName: name,
            dataTypeId,
            dataTypeValue,
            dropdownValues,
          });

      promise
        .then(() => reloadToolsTacklesCheckpointData(selectedMachine))
        .then(() => {
          setShowAddForm(false);
          setEditingPoint(null);
          resetPointForm();
          showToast(`Inspection checkpoint ${editingPoint ? 'updated' : 'added'} successfully!`, 'success');
        })
        .catch((err) => showToast(err.message || 'Failed to save', 'error'))
        .finally(() => setSavingPoint(false));
      return;
    }

    const machineForPoint = editingPoint ? (editingPoint.machine || sectionValueDisplay) : sectionValueDisplay;
    if (newPointType === 'LIST') {
      const listValues = newPointListValues.map((s) => s.trim()).filter(Boolean);
      if (editingPoint) {
        setPoints(points.map((p) =>
          p.id === editingPoint.id
            ? { ...p, name, type: 'LIST', listValues, value: listValues.join(', '), machine: machineForPoint }
            : p
        ));
      } else {
        setPoints([
          ...points,
          { id: Date.now(), name, type: 'LIST', listValues, value: listValues.join(', '), machine: machineForPoint },
        ]);
      }
    } else {
      const value = newPointValue.trim();
      if (editingPoint) {
        setPoints(points.map((p) =>
          p.id === editingPoint.id ? { ...p, name, type: newPointType, value, machine: machineForPoint } : p
        ));
      } else {
        setPoints([...points, { id: Date.now(), name, type: newPointType, value, machine: machineForPoint }]);
      }
    }
    setShowAddForm(false);
    setEditingPoint(null);
    resetPointForm();
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingPoint(null);
    resetPointForm();
  };

  const panelTitle = selectedSection && selectedType
    ? `${selectedSection} – ${selectedType === 'Identification' ? 'Identification & Description Points' : 'Inspection Checkpoints'}`
    : '';

  const sectionSelectLabel = isToolsAndTacklesSection ? 'Tools & Tackles' : 'Select Machine';
  const sectionContextLabel = isToolsAndTacklesSection ? 'Tools & Tackles' : 'Machine';

  /** Shared checkpoint/point row used by both the flat list and the grouped main-point view */
  const renderPointItem = (point) => (
    <li key={point.id} className="point-item">
      <span className="point-checkmark" aria-hidden>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
      <div className="point-content">
        <span className="point-name">{point.name}</span>
        <span className="point-type-badge">{point.type || 'TEXT'}</span>
        <span className="point-value">
          {point.type === 'LIST' && Array.isArray(point.listValues) && point.listValues.length
            ? point.listValues.join(', ')
            : point.value}
        </span>
      </div>
      <div className="point-actions">
        <button type="button" className="point-edit-button" onClick={() => handleEditPoint(point)} disabled={savingPoint}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          Edit
        </button>
        <button type="button" className="point-delete-button" onClick={() => handleDeletePoint(point.id)} disabled={savingPoint || deletingPointId === point.id} title={deletingPointId === point.id ? 'Deleting…' : 'Remove'}>
          {deletingPointId === point.id ? (
            'Deleting…'
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
              Remove
            </>
          )}
        </button>
      </div>
    </li>
  );

  return (
    <div className="settings-page">
      <div className="settings-content">
        <div className="main-content-wrapper">
          {/* Inline Manage Panel – shown when Manage is clicked */}
          {showManageModal ? (
            <div className="manage-panel">
              <header className="manage-panel-header">
                <h1 className="manage-panel-title">{panelTitle}</h1>
                <button type="button" className="manage-panel-back" onClick={handleClosePanel} aria-label="Back to settings">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Back to Settings
                </button>
              </header>

              <div className="manage-panel-body">
                <section className="points-section points-section-inline">
                  <div className="points-section-header">
                    <div className="points-section-heading-row">
                      <h2 className="points-section-title">
                        {selectedType === 'Checkpoints' ? 'Inspection Checkpoints' : 'Identification & Description Points'}
                      </h2>
                      {!showAddForm && !editingPoint && !showMainPointForm && (
                      <div className="points-section-machine-select-wrap">
                        <label htmlFor="points-select-machine" className="points-section-machine-label">{sectionSelectLabel}</label>
                        {(isToolsAndTacklesSection ? toolsTacklesError : machinesError) && (
                          <p className="points-dropdown-error" role="alert">{(isToolsAndTacklesSection ? toolsTacklesError : machinesError)}</p>
                        )}
                        <div className="select-machine-wrapper points-machine-dropdown">
                          <select
                            id="points-select-machine"
                            className="select-machine-dropdown"
                            value={selectedMachine ?? ''}
                            onChange={(e) => setSelectedMachine(e.target.value === '' ? null : Number(e.target.value))}
                            disabled={isToolsAndTacklesSection ? loadingToolsTackles : loadingMachines}
                          >
                            {(isToolsAndTacklesSection ? loadingToolsTackles : loadingMachines) ? (
                              <option value="">Loading…</option>
                            ) : (
                              <>
                                <option value="">Select…</option>
                                {machineSelectOptions.map((opt) => (
                                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                                ))}
                              </>
                            )}
                          </select>
                          <svg className="dropdown-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                      )}
                    </div>
                    {!showAddForm && !showMainPointForm && (
                      isCheckpointsMode ? (
                        <button type="button" className="add-new-point-button" onClick={handleAddMainPointClick} disabled={selectedMachine == null}>
                          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          Add Main Point
                        </button>
                      ) : (
                        <button type="button" className="add-new-point-button" onClick={() => setShowAddForm(true)}>
                          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          Add New Point
                        </button>
                      )
                    )}
                  </div>

                  {pointsError && (
                    <p className="points-dropdown-error" role="alert">{pointsError}</p>
                  )}
                  {isCheckpointsMode && mainPointsError && (
                    <p className="points-dropdown-error" role="alert">{mainPointsError}</p>
                  )}
                  {showMainPointForm ? (
                    /* ---------- Add / Edit Main Point form ---------- */
                    <div className="add-point-form">
                      <h3 className="form-title">{editingMainPoint ? 'Edit Main Point' : 'Add Main Point'}</h3>
                      <p className="form-machine-readonly">{sectionContextLabel}: <strong>{sectionValueDisplay}</strong></p>
                      <div className="form-group">
                        <label htmlFor="main-point-name">1. Main point name <span className="required">*</span></label>
                        <input
                          id="main-point-name"
                          type="text"
                          value={newMainPointName}
                          onChange={(e) => setNewMainPointName(e.target.value)}
                          placeholder="e.g., Hook Assembly, Wire Rope - Hoist Rope"
                          maxLength={255}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="main-point-order">2. Display order</label>
                        <input
                          id="main-point-order"
                          type="number"
                          value={newMainPointOrder}
                          className="main-point-order-input"
                          disabled
                          readOnly
                          aria-describedby="main-point-order-hint"
                        />
                        <p id="main-point-order-hint" className="form-hint">
                          {editingMainPoint
                            ? 'Display order is assigned automatically and cannot be changed.'
                            : 'Assigned automatically (highest existing order + 1). Main points are shown in ascending order.'}
                        </p>
                      </div>
                      <div className="form-actions-bar">
                        <div className="form-actions">
                          <button type="button" className="cancel-form-button" onClick={handleCancelMainPointForm} disabled={savingMainPoint}>
                            Cancel
                          </button>
                          <button type="button" className="save-form-button" onClick={handleSaveMainPoint} disabled={savingMainPoint || !newMainPointName.trim()}>
                            {savingMainPoint ? 'Saving…' : (editingMainPoint ? 'Update main point' : 'Save main point')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : !showAddForm ? (
                    loadingPoints || (isCheckpointsMode && loadingMainPoints) ? (
                      <p className="points-loading">
                        {selectedType === 'Checkpoints' ? 'Loading inspection checkpoints…' : 'Loading identification points…'}
                      </p>
                    ) : isCheckpointsMode ? (
                      /* ---------- Grouped main point view (machine checkpoints) ---------- */
                      groupedMainPoints.length === 0 && ungroupedCheckpoints.length === 0 ? (
                        <div className="points-empty-state">
                          <div className="points-empty-state-icon">✓</div>
                          <h3>No main points yet</h3>
                          <p>Checkpoints for <strong>{sectionValueDisplay}</strong> are organised under main points (e.g., Hook Assembly). Create your first main point to start adding inspection checkpoints.</p>
                          <button type="button" className="points-empty-state-button" onClick={handleAddMainPointClick}>
                            <span>+</span> Add your first main point
                          </button>
                        </div>
                      ) : (
                        <div className="main-points-list">
                          {groupedMainPoints.map((mp) => {
                            const isCollapsed = !!collapsedMainPoints[mp.mainPointId];
                            const count = mp.checkpoints.length;
                            return (
                              <div key={mp.mainPointId} className="main-point-group">
                                <div className="main-point-header">
                                  <button
                                    type="button"
                                    className="main-point-toggle"
                                    onClick={() => toggleMainPointCollapse(mp.mainPointId)}
                                    aria-expanded={!isCollapsed}
                                    title={isCollapsed ? 'Expand' : 'Collapse'}
                                  >
                                    <svg className={`main-point-chevron${isCollapsed ? ' collapsed' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                      <path d="M6 9L12 15L18 9" stroke="#334155" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                  <span className="main-point-order-badge" title={`Display order ${mp.displayOrder ?? '-'}`}>{mp.displayOrder ?? '-'}</span>
                                  <div className="main-point-title-block">
                                    <h3 className="main-point-name">{mp.mainPointName}</h3>
                                    <span className="main-point-count">{count} checkpoint{count !== 1 ? 's' : ''}</span>
                                  </div>
                                  <div className="main-point-actions">
                                    <button type="button" className="main-point-add-cp-button" onClick={() => handleAddCheckpointUnderMainPoint(mp.mainPointId)} disabled={savingMainPoint}>
                                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                      </svg>
                                      Add Checkpoint
                                    </button>
                                    <button type="button" className="point-edit-button" onClick={() => handleEditMainPointClick(mp)} disabled={savingMainPoint}>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                      </svg>
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      className="point-delete-button"
                                      onClick={() => handleDeleteMainPoint(mp.mainPointId)}
                                      disabled={savingMainPoint || deletingMainPointId === mp.mainPointId || count > 0}
                                      title={count > 0 ? 'Remove all checkpoints under this main point first' : 'Remove main point'}
                                    >
                                      {deletingMainPointId === mp.mainPointId ? (
                                        'Deleting…'
                                      ) : (
                                        <>
                                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                            <line x1="10" y1="11" x2="10" y2="17"/>
                                            <line x1="14" y1="11" x2="14" y2="17"/>
                                          </svg>
                                          Remove
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                                {!isCollapsed && (
                                  count === 0 ? (
                                    <div className="main-point-empty">
                                      <p>No checkpoints under this main point yet.</p>
                                      <button type="button" className="main-point-empty-add" onClick={() => handleAddCheckpointUnderMainPoint(mp.mainPointId)}>
                                        <span>+</span> Add checkpoint
                                      </button>
                                    </div>
                                  ) : (
                                    <ul className="points-list main-point-checkpoints" role="list">
                                      {mp.checkpoints.map(renderPointItem)}
                                    </ul>
                                  )
                                )}
                              </div>
                            );
                          })}
                          {ungroupedCheckpoints.length > 0 && (
                            <div className="main-point-group main-point-group-ungrouped">
                              <div className="main-point-header">
                                <span className="main-point-order-badge ungrouped-badge">!</span>
                                <div className="main-point-title-block">
                                  <h3 className="main-point-name">Not linked to a main point</h3>
                                  <span className="main-point-count">{ungroupedCheckpoints.length} checkpoint{ungroupedCheckpoints.length !== 1 ? 's' : ''} · edit each one to assign a main point</span>
                                </div>
                              </div>
                              <ul className="points-list main-point-checkpoints" role="list">
                                {ungroupedCheckpoints.map(renderPointItem)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )
                    ) : pointsForCurrentMachine.length === 0 ? (
                      <div className="points-empty-state">
                        <div className="points-empty-state-icon">✓</div>
                        <h3>{selectedType === 'Checkpoints' ? 'No inspection checkpoints' : 'No identification points'}</h3>
                        <p>No {selectedType === 'Checkpoints' ? 'checkpoints' : 'points'} for <strong>{sectionValueDisplay}</strong> yet. Add your first {selectedType === 'Checkpoints' ? 'inspection checkpoint' : 'identification point'} to get started.</p>
                        <button type="button" className="points-empty-state-button" onClick={() => setShowAddForm(true)}>
                          <span>+</span> Add your first {selectedType === 'Checkpoints' ? 'checkpoint' : 'point'}
                        </button>
                      </div>
                    ) : (
                    <ul className="points-list" role="list">
                      {pointsForCurrentMachine.map(renderPointItem)}
                    </ul>
                    )
                  ) : (
                    <div className="add-point-form">
                      <h3 className="form-title">
                        {editingPoint 
                          ? (selectedType === 'Checkpoints' ? 'Edit Inspection Checkpoint' : 'Edit Inspection Point')
                          : (selectedType === 'Checkpoints' ? 'Add Inspection Checkpoint' : 'Add Inspection Point')
                        }
                      </h3>
                      <p className="form-machine-readonly">{sectionContextLabel}: <strong>{sectionValueDisplay}</strong></p>

                      {isCheckpointsMode && (
                        <div className="form-group">
                          <label htmlFor="point-main-point">1. Main point <span className="required">*</span></label>
                          <div className="select-machine-wrapper point-type-select">
                            <select
                              id="point-main-point"
                              className="select-machine-dropdown"
                              value={newPointMainPointId ?? ''}
                              onChange={(e) => setNewPointMainPointId(e.target.value === '' ? null : Number(e.target.value))}
                            >
                              <option value="">Select main point…</option>
                              {mainPoints.map((mp) => (
                                <option key={mp.mainPointId} value={mp.mainPointId}>{mp.mainPointName}</option>
                              ))}
                            </select>
                            <svg className="dropdown-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <p className="form-hint">
                            {editingPoint
                              ? 'Changing this moves the checkpoint to another main point.'
                              : 'The checkpoint will be created under this main point.'}
                          </p>
                        </div>
                      )}

                      <div className="form-group">
                        <label htmlFor="point-name">{isCheckpointsMode ? '2. Checkpoint name' : '1. Identification point name'} <span className="required">*</span></label>
                        <input
                          id="point-name"
                          type="text"
                          value={newPointName}
                          onChange={(e) => setNewPointName(e.target.value)}
                          placeholder={isCheckpointsMode ? 'e.g., Pulley condition' : 'e.g., Machine ID, Status, Category'}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="point-type">{isCheckpointsMode ? '3. Checkpoint type' : '2. Identification point type'} <span className="required">*</span></label>
                        <div className="select-machine-wrapper point-type-select">
                          <select
                            id="point-type"
                            className="select-machine-dropdown"
                            value={newPointType}
                            onChange={handlePointTypeChange}
                          >
                            <option value="INTEGER">INTEGER</option>
                            <option value="TEXT">TEXT</option>
                            <option value="LIST">LIST</option>
                          </select>
                          <svg className="dropdown-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <p className="form-hint">INTEGER and TEXT accept a single value; LIST allows multiple values.</p>
                      </div>

                      <div className="form-group">
                        <label>
                          {isCheckpointsMode ? '4. Checkpoint value(s)' : '3. Identification point value(s)'}
                          {newPointType === 'LIST' && <span className="required"> *</span>}
                        </label>
                        {(newPointType === 'INTEGER' || newPointType === 'TEXT') ? (
                          <>
                            <input
                              id="point-value"
                              type={newPointType === 'INTEGER' ? 'number' : 'text'}
                              value={newPointValue}
                              onChange={(e) => setNewPointValue(e.target.value)}
                              placeholder={newPointType === 'INTEGER' ? 'e.g., 100' : 'e.g., LM-01 or Description'}
                              className="point-value-single"
                              disabled
                              aria-describedby="value-hint-text-integer"
                            />
                            <p id="value-hint-text-integer" className="form-hint">Not used for INTEGER or TEXT type. Values are only required for LIST.</p>
                          </>
                        ) : (
                          <div className="point-value-list-wrap">
                            <p className="form-hint list-hint">Add one value per row. Use the + button to add more.</p>
                            <ul className="point-value-list" role="list">
                              {newPointListValues.map((item, index) => (
                                <li key={index} className="point-value-list-item">
                                  <span className="point-value-list-num" aria-hidden>{index + 1}.</span>
                                  <input
                                    type="text"
                                    value={item}
                                    onChange={(e) => handleListValueChange(index, e.target.value)}
                                    placeholder={`Value ${index + 1}`}
                                    className="point-value-list-input"
                                  />
                                  <button
                                    type="button"
                                    className="point-value-list-remove"
                                    onClick={() => removeListRow(index)}
                                    disabled={newPointListValues.length <= 1}
                                    title="Remove row"
                                    aria-label="Remove row"
                                  >
                                    <span className="point-value-list-remove-icon" aria-hidden>×</span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                            <button type="button" className="add-list-row-button" onClick={addListRow}>
                              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                              Add another value
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="form-actions-bar">
                        <div className="form-actions">
                          <button type="button" className="cancel-form-button" onClick={handleCancelForm} disabled={savingPoint}>
                            Cancel
                          </button>
                          <button type="button" className="save-form-button" onClick={handleSavePoint} disabled={savingPoint || (isCheckpointsMode && newPointMainPointId == null)}>
                            {savingPoint ? 'Saving…' : (editingPoint ? 'Update point' : 'Save point')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </div>
          ) : (
            <>
          {/* Machine Section */}
          <div className="settings-section">
            <div className="section-banner machine-banner">
              <h2 className="section-title">Machine</h2>
            </div>
            <div className="section-cards">
              <div className="settings-card">
                <div className="card-icon-wrapper">
                  <div className="card-icon identification-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 4V8M16 4V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <h3 className="card-title">Identification & Description Section</h3>
                <p className="card-subtitle">Configure fields shown during inspection</p>
                <button 
                  className="manage-button"
                  onClick={() => handleManageClick('Machine', 'Identification')}
                >
                  Manage
                </button>
              </div>
              <div className="settings-card">
                <div className="card-icon-wrapper">
                  <div className="card-icon checkpoint-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>
                <h3 className="card-title">Inspection Checkpoints Section</h3>
                <p className="card-subtitle">Configure fields shown during inspection</p>
                <button 
                  className="manage-button"
                  onClick={() => handleManageClick('Machine', 'Checkpoints')}
                >
                  Manage
                </button>
              </div>
            </div>
          </div>

          {/* Tools And Tackles Section */}
          <div className="settings-section">
            <div className="section-banner tools-banner">
              <h2 className="section-title">Tools And Tackles</h2>
            </div>
            <div className="section-cards">
              <div className="settings-card">
                <div className="card-icon-wrapper">
                  <div className="card-icon identification-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 4V8M16 4V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <h3 className="card-title">Identification & Description Section</h3>
                <p className="card-subtitle">Configure fields shown during inspection</p>
                <button 
                  className="manage-button"
                  onClick={() => handleManageClick('Tools And Tackles', 'Identification')}
                >
                  Manage
                </button>
              </div>
              <div className="settings-card">
                <div className="card-icon-wrapper">
                  <div className="card-icon checkpoint-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                </div>
                <h3 className="card-title">Inspection Checkpoints Section</h3>
                <p className="card-subtitle">Configure fields shown during inspection</p>
                <button 
                  className="manage-button"
                  onClick={() => handleManageClick('Tools And Tackles', 'Checkpoints')}
                >
                  Manage
                </button>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
      <ActionSnackbar toast={toast} onClose={handleCloseToast} />
    </div>
  );
}

export default Settings;

